import { z } from 'zod'

/**
 * A `FieldConfig.percent` field (`ResourceTable.tsx`) always stores/PATCHes a
 * raw decimal fraction (e.g. `0.15`), but every edit surface — `EditableTable`'s
 * inline-edit input and `ResourceForm`'s `PercentInput` — displays/writes the
 * percentage (`15`). Both share this conversion so a fix (e.g. coercing a
 * numeric string, which `NumberField`'s own `defaultTransform` already does
 * for read-mode display — HATCH-69 review: read mode showed `15%` from an API
 * string while the inline-edit input went blank, since it required a strict
 * `typeof value === 'number'`) only has to happen once.
 */

/** A stored fraction, possibly a numeric string from the API, into the percentage an input displays (`0.15`/`"0.15"` -> `15`); anything non-numeric becomes `''` so a controlled number input doesn't warn about switching from `""` to a number. */
export function percentDisplayValue(value: unknown): number | '' {
  const numeric =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value !== '' && !Number.isNaN(Number(value))
        ? Number(value)
        : null
  return numeric == null ? '' : numeric * 100
}

/** The inverse of `percentDisplayValue` — a typed percentage (`15`) back to the decimal fraction the API expects (`0.15`); `NaN` (cleared input) becomes `null`. */
export function percentToFraction(displayValue: number): number | null {
  return Number.isNaN(displayValue) ? null : displayValue / 100
}

/**
 * Every `FieldConfig.percent` schema field's own type (HATCH-226: "Discount
 * percent is not savable" — `Expected number, received string` saving a
 * product without touching that field). A percent field is `z.number()` by
 * convention (unlike `apps/products/schema.ts`'s money fields, which stay
 * strings for `decimalField()`'s `TextInput`, a percent field's stored
 * fraction has to be a number for `percentDisplayValue`/`percentToFraction`'s
 * own math) — but same as those money fields, the real API can return this
 * fraction as a numeric string (e.g. `"0.1"`) on fetch, and an untouched
 * field keeps that raw string in react-hook-form state regardless of
 * whether the field itself was edited, failing `z.number()` on submit no
 * matter what was actually changed. One `z.preprocess` coercing a string to
 * a number before the `z.number()` check, reused by every percent field
 * (`apps/products`' `discount_percent`, `apps/providers`' `rate`,
 * `apps/discount-codes`' `percent`, `apps/cart-based-discounts`'
 * `discount_percent`, `apps/users`' `referrer_discount_percent`) instead of
 * each schema growing its own copy of the same fix (ticket feedback: "do i
 * need to write some custom code to handle percent each time?").
 */
export function percentField(options?: { nullish?: boolean }) {
  const numberSchema = z.preprocess(
    (value) => (typeof value === 'string' && value !== '' ? Number(value) : value),
    z.number(),
  )
  return options?.nullish ? numberSchema.nullish() : numberSchema
}
